import { Trans, useLingui } from '@lingui/react/macro';
import type { ScalingConfig, StaffAssignmentConfig, StaffFilters } from '@repo/contracts';
import { useFlag } from '@repo/flags/react';
import {
  Briefcase,
  Calendar,
  ChevronDown,
  Clock,
  Hand,
  type LucideIcon,
  Sparkles,
  Tag as TagIcon,
  UserRound,
  Users,
  WandSparkles,
} from 'lucide-react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DayLabel } from './day-label';
import { ShiftTypeSelect } from './shift-type-select';
import {
  DAY_COLUMNS,
  daysToSchedule,
  detectDayPattern,
  scheduleToDays,
  weekdayShortLabel,
} from '@/domain/templates/day-of-week';
import {
  filterEligibleEmployees,
  reconcileFixedEmployeeId,
  reconcileSelectedIds,
} from '@/domain/templates/shift-staffing-selection';
import { cn, filterByIds, toSelectOptions } from '@/lib/utils';
import type {
  DayPattern,
  DemandScaling,
  Employee,
  ShiftSet,
  ShiftType,
  StaffGroup,
  Tag,
} from '@/types/template';

import { ScalingControls } from './scaling-controls';

interface PatternFormProps {
  /** Pattern to edit, or null/undefined to add a new one. */
  initial?: ShiftSet | null;
  /** Shift types for the type selector. */
  shiftTypes: ShiftType[];
  /** Staff groups available to restrict the shift to (scoped to the template's sites). */
  availableStaffGroups?: StaffGroup[];
  /** Tags available to restrict the shift to. */
  availableTags?: Tag[];
  /** Employees available to restrict to, or to name as the specific assignee. */
  availableEmployees?: Employee[];
  /** Emits the assembled pattern (with a minted id when new). */
  onSave: (shift: ShiftSet) => void;
  /** Abandons the form without saving. */
  onCancel: () => void;
  /**
   * Emits the in-progress pattern on every change so the live preview can show
   * a draft (ghost) version while the form is open. Pass a stable reference.
   */
  onDraftChange?: (draft: ShiftSet) => void;
  /** Emits whether the form is currently savable, so a parent footer can disable its Save button. */
  onCanSaveChange?: (canSave: boolean) => void;
  /** Hide the form's footer buttons when the parent provides its own footer. */
  hideFooter?: boolean;
  /** Exposed save function for parent footer to call when hideFooter is true. */
  saveRef?: React.RefObject<(() => void) | null>;
  /** Template demand-scaling settings; when enabled, the count field becomes the scaling controls. */
  demandScaling?: DemandScaling;
}

type WorkingPattern = Omit<ShiftSet, 'id'> & { id?: string };
type StaffingMethod = StaffAssignmentConfig['method'];

const PRESETS: Array<Exclude<DayPattern, 'custom'>> = ['weekdays', 'weekends', 'everyday'];
const STAFFING_METHODS: StaffingMethod[] = ['automatic', 'open', 'specific'];

/** Common shift windows offered as one-click chips below the time inputs. */
const TIME_SUGGESTIONS: ReadonlyArray<readonly [start: string, end: string]> = [
  ['09:00', '17:00'],
  ['14:00', '22:00'],
  ['18:00', '02:00'],
  ['11:00', '16:00'],
  ['16:00', '23:00'],
];

/**
 * Inline add/edit form for a shift pattern in the Build workspace left pane —
 * the two-pane replacement for the wizard's `ShiftModal`. Edits the contract
 * shapes directly: a `ShiftSchedule` via the day picker and a
 * `StaffAssignmentConfig` discriminated union (auto-assign / open carry a count
 * and restriction filters; specific names a single employee).
 */
export function PatternForm({
  initial,
  shiftTypes,
  availableStaffGroups = [],
  availableTags = [],
  availableEmployees = [],
  onSave,
  onCancel,
  onDraftChange,
  onCanSaveChange,
  hideFooter = false,
  saveRef,
  demandScaling,
}: PatternFormProps) {
  const { t, i18n } = useLingui();
  const [isScalingEnabled] = useFlag('TEMPLATE_BUILDER_SCALING');
  const [working, setWorking] = useState<WorkingPattern>(initial ?? emptyPattern());
  // Whether the custom day picker is shown. Starts open when the initial days
  // don't match a named preset; otherwise the user opts in via the Custom pill.
  const [customMode, setCustomMode] = useState(
    () => detectDayPattern(scheduleToDays((initial ?? emptyPattern()).schedule)) === 'custom',
  );

  // Push the in-progress pattern to the live preview as it changes (ghost pill).
  useEffect(() => {
    // `||` not `??`: a starter prefill carries an empty-string id, which must
    // still fall back to the draft placeholder.
    onDraftChange?.({ ...working, id: working.id || 'draft' });
  }, [working, onDraftChange]);

  // Labels live in component scope so the Lingui `t` macro transforms them.
  const presetLabels: Record<Exclude<DayPattern, 'custom'>, string> = {
    weekdays: t`Weekdays`,
    weekends: t`Weekends`,
    everyday: t`Every day`,
  };
  const methodMeta: Record<StaffingMethod, { icon: LucideIcon; title: string; hint: string }> = {
    automatic: { icon: WandSparkles, title: t`Auto-assign`, hint: t`Algorithm picks fairly` },
    open: { icon: Hand, title: t`Open shift`, hint: t`Assign manually later` },
    specific: { icon: UserRound, title: t`Specific person`, hint: t`Same person each time` },
  };

  const staffing = working.staffing;
  const days = scheduleToDays(working.schedule);
  // The auto-staffing count field becomes the scaling controls when the template
  // scales to demand (gated by the flag too, in case a saved template carries it).
  const scalingEnabled = isScalingEnabled && demandScaling?.enabled === true;

  // Restrictions apply to automatic + open (the rotagen mapper feeds both
  // through `buildStaffFilters`); `specific` instead names a single employee.
  const showRestrictions = staffing.method === 'automatic' || staffing.method === 'open';
  const filters = staffing.method === 'specific' ? undefined : staffing.filters;
  // Memoised so the `?? []` fallbacks return a stable reference and don't
  // re-trigger the `eligibleEmployees` memo on every render.
  const selectedGroups = useMemo(() => filters?.staffGroups ?? [], [filters?.staffGroups]);
  const selectedTags = useMemo(() => filters?.tags ?? [], [filters?.tags]);
  const selectedEmployees = filters?.specificEmployees ?? [];
  const fixedEmployeeId = reconcileFixedEmployeeId(
    staffing.method === 'specific' ? staffing.employee : undefined,
    availableEmployees,
  );

  // The specific-employee picker only offers people eligible under the chosen
  // group/tag filters, recomputed when either changes.
  const eligibleEmployees = useMemo(
    () => filterEligibleEmployees(availableEmployees, selectedGroups, selectedTags),
    [availableEmployees, selectedGroups, selectedTags],
  );

  const isValid = Boolean(
    working.name.trim() &&
      working.shiftType.id &&
      days.length > 0 &&
      working.startTime &&
      working.endTime &&
      (staffing.method !== 'specific' || fixedEmployeeId),
  );

  // When editing an existing pattern, require an actual change before saving.
  // A starter prefill carries an empty id, so it counts as adding (always
  // dirty). Adding from scratch (no initial) is likewise always saveable.
  const isEditing = Boolean(initial?.id);
  const isDirty = !initial || stableStringify(working) !== stableStringify(initial);
  const canSave = isValid && (!isEditing || isDirty);

  // Surface validity to a parent that renders its own footer Save button.
  useEffect(() => {
    onCanSaveChange?.(canSave);
  }, [canSave, onCanSaveChange]);

  function patch<Key extends keyof WorkingPattern>(key: Key, value: WorkingPattern[Key]) {
    setWorking((prev) => ({ ...prev, [key]: value }));
  }

  /** Switches the staffing method, reshaping the discriminated union member. */
  function setMethod(method: StaffingMethod) {
    setWorking((prev) => ({ ...prev, staffing: reshapeStaffing(prev.staffing, method) }));
  }

  /** Sets the head count for an open/automatic shift. */
  function setCount(count: number) {
    setWorking((prev) => {
      if (prev.staffing.method === 'specific') return prev;
      return { ...prev, staffing: { ...prev.staffing, count: Math.max(1, count || 1) } };
    });
  }

  /**
   * Updates a group/tag filter and prunes any specific-employee selections that
   * are no longer eligible under the new filter combination.
   */
  function setFilter(key: 'staffGroups' | 'tags', objects: StaffGroup[] | Tag[]) {
    setWorking((prev) => {
      if (prev.staffing.method === 'specific') return prev;
      const nextFilters: StaffFilters = { ...prev.staffing.filters, [key]: objects };
      const eligibleIds = new Set(
        filterEligibleEmployees(
          availableEmployees,
          nextFilters.staffGroups ?? [],
          nextFilters.tags ?? [],
        ).map((e) => e.id),
      );
      nextFilters.specificEmployees = (nextFilters.specificEmployees ?? []).filter((e) =>
        eligibleIds.has(e.id),
      );
      return { ...prev, staffing: { ...prev.staffing, filters: nextFilters } };
    });
  }

  /** Sets the specific-employee restriction list for an open/automatic shift. */
  function setSpecificEmployees(employees: Employee[]) {
    setWorking((prev) => {
      if (prev.staffing.method === 'specific') return prev;
      return {
        ...prev,
        staffing: {
          ...prev.staffing,
          filters: { ...prev.staffing.filters, specificEmployees: employees },
        },
      };
    });
  }

  /** Sets (or clears, when `undefined`) the scaling config on an open/automatic shift. */
  function setScaling(next: ScalingConfig | undefined) {
    setWorking((prev) => {
      if (prev.staffing.method === 'specific') return prev;
      const staffingNext = { ...prev.staffing };
      if (next === undefined) {
        delete staffingNext.scaling;
      } else {
        staffingNext.scaling = next;
      }
      return { ...prev, staffing: staffingNext };
    });
  }

  /** Sets the single assigned employee for a `specific` shift. */
  function setFixedEmployee(id: string) {
    const employee = availableEmployees.find((candidate) => candidate.id === id);
    setWorking((prev) => ({
      ...prev,
      staffing: { method: 'specific', employee: { id, name: employee?.name ?? id } },
    }));
  }

  function selectPreset(pattern: Exclude<DayPattern, 'custom'>) {
    setCustomMode(false);
    patch('schedule', pattern);
  }

  function toggleDay(day: number) {
    setWorking((prev) => {
      const current = scheduleToDays(prev.schedule);
      const next = current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day].sort((a, b) => a - b);
      return { ...prev, schedule: daysToSchedule(next) };
    });
  }

  const handleSave = useCallback(() => {
    // `||` not `??`: starter prefills carry an empty-string id, which must still
    // mint a fresh id on save (otherwise multiple starter shifts collide on id).
    onSave({ ...working, id: working.id || crypto.randomUUID(), name: working.name.trim() });
  }, [working, onSave]);

  // Expose save function to parent via ref when hideFooter is true
  useImperativeHandle(
    saveRef,
    () => () => {
      if (canSave) {
        handleSave();
      }
    },
    [canSave, handleSave],
  );

  return (
    <div className="space-y-4 p-4 rotaready:space-y-5">
      <div className="space-y-2">
        <Label htmlFor="pattern-name" className="rotaready:gap-0 rotaready:text-base">
          <TagIcon className="size-3.5 text-muted-foreground rotaready:hidden" />
          <Trans>Shift name</Trans>
          <RequiredMark />
        </Label>
        <Input
          id="pattern-name"
          value={working.name}
          onChange={(e) => patch('name', e.target.value)}
          placeholder={t`e.g. Morning bar`}
        />
      </div>

      <div className="space-y-2">
        <Label className="rotaready:gap-0 rotaready:text-base">
          <Briefcase className="size-3.5 text-muted-foreground rotaready:hidden" />
          <Trans>Shift type</Trans>
          <RequiredMark />
        </Label>
        <ShiftTypeSelect
          shiftTypes={shiftTypes}
          value={working.shiftType.id}
          onChange={(id, name) => patch('shiftType', { id, name })}
        />
        {shiftTypes.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-2">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Sparkles className="size-3" />
              <Trans>Suggestions</Trans>
            </span>
            {shiftTypes.slice(0, 4).map((type) => (
              <Button
                key={type.id}
                type="button"
                variant="secondary"
                size="xs"
                className="rounded-full"
                onClick={() => patch('shiftType', { id: type.id, name: type.name })}
              >
                {type.name}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="rotaready:gap-0 rotaready:text-base">
          <Calendar className="size-3.5 text-muted-foreground rotaready:hidden" />
          <Trans>Days</Trans>
          <RequiredMark />
        </Label>
        <div className="flex gap-1">
          {PRESETS.map((preset) => (
            <Button
              key={preset}
              type="button"
              size="sm"
              variant={!customMode && working.schedule === preset ? 'default' : 'outline'}
              onClick={() => selectPreset(preset)}
            >
              {presetLabels[preset]}
            </Button>
          ))}
          <Button
            type="button"
            size="sm"
            variant={customMode ? 'default' : 'outline'}
            aria-pressed={customMode}
            onClick={() => {
              setCustomMode(true);
              patch('schedule', daysToSchedule([]));
            }}
          >
            <Trans>Custom</Trans>
          </Button>
        </div>
        {customMode && (
          <div className="flex flex-wrap gap-1.5">
            {DAY_COLUMNS.map((column) => {
              const active = days.includes(column.dayOfWeek);
              return (
                <Button
                  key={column.key}
                  type="button"
                  size="icon"
                  className="size-9 rounded-full"
                  variant={active ? 'default' : 'outline'}
                  aria-pressed={active}
                  aria-label={weekdayShortLabel(column.dayOfWeek, i18n.locale)}
                  onClick={() => toggleDay(column.dayOfWeek)}
                >
                  <DayLabel columnKey={column.key} variant="narrow" />
                </Button>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="rotaready:gap-0 rotaready:text-base">
          <Clock className="size-3.5 text-muted-foreground rotaready:hidden" />
          <Trans>Start &amp; end time</Trans>
        </Label>
        <div className="grid grid-cols-2 gap-3">
          <Input
            id="pattern-start"
            type="time"
            aria-label={t`Start time`}
            value={working.startTime}
            onChange={(e) => patch('startTime', e.target.value)}
          />
          <Input
            id="pattern-end"
            type="time"
            aria-label={t`End time`}
            value={working.endTime}
            onChange={(e) => patch('endTime', e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Sparkles className="size-3" />
            <Trans>Suggestions</Trans>
          </span>
          {TIME_SUGGESTIONS.map(([start, end]) => (
            <Button
              key={`${start}-${end}`}
              type="button"
              variant="secondary"
              size="xs"
              className="rounded-full"
              onClick={() => setWorking((prev) => ({ ...prev, startTime: start, endTime: end }))}
            >
              {start}–{end}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="rotaready:gap-0 rotaready:text-base">
          <Users className="size-3.5 text-muted-foreground rotaready:hidden" />
          <Trans>Who works this shift?</Trans>
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {STAFFING_METHODS.map((method) => {
            const meta = methodMeta[method];
            const Icon = meta.icon;
            const active = staffing.method === method;
            return (
              <button
                key={method}
                type="button"
                aria-pressed={active}
                aria-label={meta.title}
                onClick={() => setMethod(method)}
                className={cn(
                  'flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-center transition-colors',
                  active
                    ? 'border-primary bg-accent shadow-[0_0_0_1px_var(--color-primary)]'
                    : 'hover:bg-accent',
                )}
              >
                <Icon className={cn('size-5', active ? 'text-primary' : 'text-muted-foreground')} />
                <span className="text-[length:var(--small-font-size)] leading-[var(--small-line-height)] font-semibold">
                  {meta.title}
                </span>
                <span className="text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] text-muted-foreground">
                  {meta.hint}
                </span>
              </button>
            );
          })}
        </div>
        {staffing.method === 'automatic' && (
          <div className="bg-muted rounded-lg border px-3.5 pt-3.5 pb-0 space-y-3.5">
            {scalingEnabled && demandScaling ? (
              <ScalingControls
                metric={demandScaling.metric}
                count={staffing.count}
                onCountChange={setCount}
                scaling={staffing.scaling}
                onScalingChange={setScaling}
              />
            ) : (
              <div className="flex items-center gap-2">
                <Label htmlFor="pattern-count" className="rotaready:text-base">
                  <Trans>Number of people</Trans>
                </Label>
                <Input
                  id="pattern-count"
                  type="number"
                  min={1}
                  className="w-20"
                  value={staffing.count}
                  onChange={(e) => setCount(Number(e.target.value))}
                />
              </div>
            )}

            {showRestrictions && (
              <Collapsible className="space-y-3">
                <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 border-t border-dashed pt-3.5 text-sm font-medium text-primary">
                  <ChevronDown className="size-4 shrink-0" />
                  <Trans>Restrict who can be assigned</Trans>
                  <span className="text-muted-foreground font-normal text-xs">
                    <Trans>(optional)</Trans>
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3 pb-3.5">
                  <div className="space-y-1.5">
                    <Label className="rotaready:text-base">
                      <Trans>Staff groups</Trans>
                    </Label>
                    <MultiSelect
                      options={toSelectOptions(availableStaffGroups)}
                      value={reconcileSelectedIds(selectedGroups, availableStaffGroups)}
                      onValueChange={(ids) =>
                        setFilter('staffGroups', filterByIds(availableStaffGroups, ids))
                      }
                      placeholder={t`Any staff group`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="rotaready:text-base">
                      <Trans>Tags</Trans>
                    </Label>
                    <MultiSelect
                      options={toSelectOptions(availableTags)}
                      value={reconcileSelectedIds(selectedTags, availableTags)}
                      onValueChange={(ids) => setFilter('tags', filterByIds(availableTags, ids))}
                      placeholder={t`Any tag`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="rotaready:text-base">
                      <Trans>Specific employees</Trans>
                    </Label>
                    <MultiSelect
                      options={toSelectOptions(eligibleEmployees)}
                      value={reconcileSelectedIds(selectedEmployees, eligibleEmployees)}
                      onValueChange={(ids) =>
                        setSpecificEmployees(filterByIds(availableEmployees, ids))
                      }
                      placeholder={t`Anyone eligible`}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}

        {staffing.method === 'specific' && (
          <div className="space-y-2">
            <Label htmlFor="pattern-fixed-employee" className="rotaready:gap-0 rotaready:text-base">
              <Trans>Assigned employee</Trans>
              <RequiredMark />
            </Label>
            <Select value={fixedEmployeeId} onValueChange={setFixedEmployee}>
              <SelectTrigger id="pattern-fixed-employee" className="w-full">
                <SelectValue placeholder={t`Select an employee…`} />
              </SelectTrigger>
              <SelectContent>
                {availableEmployees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!hideFooter && (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            <Trans>Save</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}

/** Red asterisk marking a required field. Hidden from the accessibility tree
 * (the empty-field validation drives the disabled Save button). */
function RequiredMark() {
  return (
    <span aria-hidden className="text-destructive">
      *
    </span>
  );
}

/**
 * Order-independent JSON serialisation, used to detect whether the form has
 * changed from its initial pattern (object key order can vary as the working
 * copy is rebuilt, so a plain `JSON.stringify` compare would be unreliable).
 */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value);
}

/** A blank pattern, defaulted to a weekday day-time shift staffed automatically. */
function emptyPattern(): WorkingPattern {
  return {
    name: '',
    schedule: 'weekdays',
    shiftType: { id: '', name: '' },
    startTime: '09:00',
    endTime: '17:00',
    staffing: { method: 'automatic', count: 1, filters: {} },
  };
}

/**
 * Reshapes the staffing config to the target method, preserving the count,
 * filters and scaling across open↔automatic and carrying nothing into specific
 * (which has no count to scale).
 */
function reshapeStaffing(
  current: StaffAssignmentConfig,
  method: StaffingMethod,
): StaffAssignmentConfig {
  const count = current.method === 'specific' ? 1 : current.count;
  const filters = current.method === 'specific' ? {} : (current.filters ?? {});
  const scaling = current.method === 'specific' ? undefined : current.scaling;
  const scalingPart = scaling ? { scaling } : {};

  switch (method) {
    case 'open':
      return { method: 'open', count, filters, ...scalingPart };
    case 'automatic':
      return { method: 'automatic', count, filters, ...scalingPart };
    case 'specific':
      return {
        method: 'specific',
        employee: current.method === 'specific' ? current.employee : { id: '', name: '' },
      };
  }
}
